import './MapControl.scss';

import React, { useContext, useEffect, useState } from 'react';
import useTriggerEffect from 'react-use-trigger/useTriggerEffect';
import AppContext from '../store/app-context';

import MapIcon from '@mui/icons-material/Map';
import { Button, Tooltip } from '@mui/material';

import { distanceArray } from './SelectDistance';

export default function MapControl(props: any) {
  const { apiRef, geoFenceLatitude, geoFenceLongitude, geoFenceRadiusInMiles, getShiftHeld, triggers, rowId, isOpened } = props;
  const type = triggers.type;
  const closeMapTrigger = triggers.close;
  const openMapTrigger = triggers.open;
  const [showMap, setShowMap] = useState<boolean>(isOpened);
  const [zoom, setZoom] = useState<string>('14');
  const [initialized, setInitialized] = useState<boolean>(false);
  const { listOpenMapIds, setListOpenMapIds }: { listOpenMapIds: Array<object>, setListOpenMapIds: Function } = useContext(AppContext);

  // Here we have the two map column widths, one for when we just have the Show Map button (narrowMapColumnWidth), and the other
  // for when the map is displayed (wideMapColumnWidth).
  const narrowMapColumnWidth = 155;
  const wideMapColumnWidth = 500;

  const getVisibleRowIds = () => {
    // Build array of visible ids.
    const state = apiRef.current.state;
    const rows = state.rows;
    const pagination = state.pagination;
    const page = pagination.page;
    const pageSize = pagination.pageSize;
    const rowCount = pagination.rowCount;
    const startIndex = rowCount > 0 ? page * pageSize : -1;
    const endIndex = startIndex >= 0 ? startIndex + (Math.min(rowCount - page * pageSize, pageSize) - 1) : -1;

    // Build array of visible ids
    const visibleIds: Array<any> = [];
    if (startIndex >= 0) {
      for (let i = startIndex; i <= endIndex; i++) {
        visibleIds.push(rows.ids[i]);
      }
    }
    return visibleIds;
  }

  const atLeastOneVisibleMapIsShowing = () => {
    // Build array of visible ids.
    const state = apiRef.current.state;
    const rows = state.rows;
    const pagination = state.pagination;
    const page = pagination.page;
    const pageSize = pagination.pageSize;
    const rowCount = pagination.rowCount;
    const startIndex = rowCount > 0 ? page * pageSize : -1;
    const endIndex = startIndex >= 0 ? startIndex + (Math.min(rowCount - page * pageSize, pageSize) - 1) : -1;

    // Build array of visible ids
    const visibleIds: Array<any> = [];
    if (startIndex >= 0) {
      for (let i = startIndex; i <= endIndex; i++) {
        visibleIds.push(rows.ids[i]);
      }
    }

    // Find intersection between the array of visible ids and open map ids.
    const intersection = visibleIds.filter(visibleId => listOpenMapIds.findIndex((obj: any) => obj.rowId === visibleId) !== -1);
    return intersection.length > 0;
  }

  const addOpenMapId = (rowId: string) => {
    const indexRowId = listOpenMapIds.findIndex((obj: any) => obj.rowId === rowId);
    if (indexRowId < 0) {
      setListOpenMapIds(listOpenMapIds.concat({ rowId: rowId, type: type }));
      const width = apiRef.current.getColumn('map').width;
      if (width !== wideMapColumnWidth) {
        apiRef.current.setColumnWidth('map', wideMapColumnWidth);
      }
    }
  }

  const deleteOpenMapId = (rowId: string) => {
    const newList = listOpenMapIds.filter((obj: any) => obj.rowId !== rowId);
    setListOpenMapIds(newList);
    // Check to see if we have any entries for the given type: pending, scheduled, submitted.
    const subList = newList.filter((obj: any) => obj.type === type);

    if (subList.length === 0) {
      const width = apiRef.current.getColumn('map').width;
      if (width !== narrowMapColumnWidth) {
        apiRef.current.setColumnWidth('map', narrowMapColumnWidth);
      }
    }
  }

  useEffect(() => {
    if (showMap) {
      addOpenMapId(rowId);
    } else {
      deleteOpenMapId(rowId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap])

  useEffect(() => {
    if (geoFenceRadiusInMiles) {
      const indexDistance = distanceArray.findIndex((distance) => distance.value === ('' + geoFenceRadiusInMiles));
      if (indexDistance >= 0) {
        const z = distanceArray[indexDistance].zoom;
        setZoom(z);
      }
    }
  }, [geoFenceRadiusInMiles])

  useTriggerEffect(() => {
    if (initialized && (openMapTrigger.type === type)) {
      const hasMap = rowHasNonZeroLatLong(rowId);
      if (hasMap) {
        addOpenMapId(rowId);
        setShowMap(true);
      }
    }
  }, openMapTrigger);

  useTriggerEffect(() => {
    if (initialized && (closeMapTrigger.type === type)) {
      deleteOpenMapId(rowId);
      setShowMap(false);
    }
  }, closeMapTrigger);

  useEffect(() => {
    const index = listOpenMapIds.findIndex((obj: any) => obj.rowId === rowId);
    if (index >= 0) {
      setShowMap(true);
    }

    // Prevent the triggers from being acted upon until after we reach this point in the initialization of the component.
    setInitialized(true);

    // If there is at least one visible row with an open map element then we should widen the Map column in the grid
    // to display the map better, else, make the map column narrow since it is only displaying the Show Map buttons.
    setTimeout(() => {
      const aVisibleMapIsShowing = atLeastOneVisibleMapIsShowing();
      const width = apiRef.current.getColumn('map').width;

      if (aVisibleMapIsShowing) {
        if (width !== wideMapColumnWidth) {
          apiRef.current.setColumnWidth('map', wideMapColumnWidth);
        }
      } else {
        if (width !== narrowMapColumnWidth) {
          apiRef.current.setColumnWidth('map', narrowMapColumnWidth);
        }
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeAllMapIdsOfGivenType = (type: string) => {
    const newList = listOpenMapIds.filter((obj: any) => obj.type !== type);
    setListOpenMapIds(newList);
  }

  const rowHasNonZeroLatLong = (rowId: string) => {
    const idRowsLookup = apiRef.current.state.rows.idRowsLookup;
    const row = idRowsLookup[rowId];
    if (row) {
      const hasLat = row.geoFenceLatitude !== 0;
      const hasLong = row.geoFenceLongitude !== 0;
      return (hasLat && hasLong);
    }
    return false;
  }

  const addAllVisibleMapIdsOfGivenType = (type: string) => {
    const visibleRowIds = getVisibleRowIds();
    const visibleRowIdsWithLatLong = visibleRowIds.filter((rowId) => rowHasNonZeroLatLong(rowId));
    visibleRowIdsWithLatLong.forEach((rowId) => addOpenMapId(rowId));
  }

  const mapSrc = `https://maps.google.com/maps?q=${geoFenceLatitude},${geoFenceLongitude}&z=${zoom}&ie=UTF8&iwloc=&output=embed`;
  return (
    <>
      {(geoFenceLatitude !== 0) && (geoFenceLongitude !== 0) && (geoFenceRadiusInMiles > 0) &&
        <><Tooltip arrow title={'Press shift key while clicking to open/close all maps.'} enterDelay={1000}>
          <Button variant="outlined" endIcon={<MapIcon className={'map-icon'} />} onClick={(event) => {
            event.stopPropagation();
            if (getShiftHeld()) {
              if (showMap) {
                removeAllMapIdsOfGivenType(type);
                closeMapTrigger();
              } else {
                setTimeout(() => openMapTrigger(), 0);
                addAllVisibleMapIdsOfGivenType(type);
              }
            } else {
              setShowMap(!showMap);
            }
          }}>
            {showMap ? 'Hide Map' : 'Show Map'}
          </Button>
        </Tooltip>
          {showMap &&
            <div className="gmap_canvas_table">
              <iframe className={'iframe-map'} title="map" width="478" height="478" id="gmap_canvas" src={mapSrc} frameBorder={"0"} scrolling="no" marginHeight={0} marginWidth={0}>
              </iframe>
            </div>
          }
        </>
      }
    </>
  );
}